import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CasdoorAuthService } from 'app/core/auth/casdoor-auth.service';
import { Capacitor } from '@capacitor/core';
import { AuthAlertService } from 'app/core/auth/auth-alert.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { UserService } from 'app/core/user/user.service';
import { NoRoleAssignedError, UserProfileService } from 'app/modules/dashboard/dipita/user-profile/user-profile.service';
import { getDefaultRedirectPath } from 'app/modules/dashboard/dipita/user-profile/user-profile.types';
import { PatientService } from 'app/modules/dashboard/dipita/requester/patient/patient.service';
import { PatientCacheService } from 'app/modules/dashboard/dipita/requester/patient/patient-cache.services';
import { RequestService } from 'app/modules/dashboard/dipita/requester/request/request.service';
import { ExpertRequestService } from 'app/modules/dashboard/dipita/expert/shared/services/expert-request.service';
import { firstValueFrom } from 'rxjs';

/**
 * Service to handle authentication initialization on app startup
 * Prevents loading loops and manages proper routing based on auth state
 */
@Injectable({
    providedIn: 'root'
})
export class AuthInitializationService {
    private _authService = inject(CasdoorAuthService);
    private _router = inject(Router);
    private _userService = inject(UserService);
    private _userProfileService = inject(UserProfileService);
    private _patientService = inject(PatientService);
    private _patientCacheService = inject(PatientCacheService);
    private _requestService = inject(RequestService);
    private _expertRequestService = inject(ExpertRequestService);
    private _authAlertService = inject(AuthAlertService);
    private _navigationService = inject(NavigationService);
    private _document = inject(DOCUMENT);
    private _ngZone = inject(NgZone);
    private _isInitialized = false;

    async initialize(): Promise<void> {
        if (this._isInitialized) {
            return;
        }

        this._isInitialized = true;

        try {
            const isAuthenticated = await firstValueFrom(this._authService.isAuthenticated$);
            console.log('****************** isAuthenticated:', isAuthenticated);

            if (!isAuthenticated) {
                return;
            }

            const currentUrl = this._router.url;

            if (currentUrl.startsWith('/dashboard')) {
                return;
            }

            // Check if on sign-in (with or without query params like ?code=...&state=...)
            if (currentUrl.startsWith('/sign-in')) {
                await this.handleAuthenticationSuccess();
            }

        } catch (error) {
            console.error('Error during auth initialization:', error);
        }
    }

    async handleAuthenticationSuccess(): Promise<void> {
        try {
            const isAuthenticated = await firstValueFrom(this._authService.isAuthenticated$);
            if (!isAuthenticated) {
                await this.navigateTo('/sign-in');
                return;
            }

            try {
                // An empty profile is no longer a blocking state: the user lands in the app and
                // completes it from Settings. Only the role matters here, to pick the landing page.
                try {
                    const profile = await this._userProfileService.getProfile();
                    if (profile?.roles?.length) {
                        // Ensure navigation is refreshed with the latest roles before navigating
                        await firstValueFrom(this._navigationService.get());
                        const redirectPath = getDefaultRedirectPath(profile.roles);
                        await this.navigateTo(redirectPath);
                        return;
                    }
                } catch (profileError) {
                    if (profileError instanceof NoRoleAssignedError || profileError.name === 'NoRoleAssignedError') {
                        this.handleNoRoleError();
                        return;
                    }
                    console.warn('Could not load full profile, using default redirect');
                }

                // Fallback: profile missing or without roles
                await firstValueFrom(this._navigationService.get());
                await this.navigateTo('/dashboard/requester/request');

            } catch (statusError) {
                if (statusError instanceof NoRoleAssignedError || statusError?.name === 'NoRoleAssignedError') {
                    this.handleNoRoleError();
                    return;
                }
                // On error, default to dashboard and let guards handle it
                console.warn('Could not load profile status, using default redirect:', statusError);
                // Try to refresh navigation anyway to avoid stale menus after account switches
                try { await firstValueFrom(this._navigationService.get()); } catch { }
                await this.navigateTo('/dashboard');
            }

        } catch (error) {
            console.error('Error handling authentication success:', error);
            // Attempt to refresh navigation to clear stale cache
            try { await firstValueFrom(this._navigationService.get()); } catch { }
            await this.navigateTo('/dashboard');
        } finally {
            this._authService.isAuthLoading.set(false);
        }
    }

    private async navigateTo(path: string): Promise<void> {
        await this._ngZone.run(async () => {
            const success = await this._router.navigate([path], { replaceUrl: true });
            console.log(`Post-login navigation to ${path}:`, success ? 'ok' : 'failed', `(current: ${this._router.url})`);
        });
    }


    private handleNoRoleError(): void {
        this._authAlertService.clearSessionExpiredAlert();
        this._authAlertService.showAccountPendingAlert();

        // Clear all cached user data before logout
        this._userService.clearUser();
        this._userProfileService.clearProfileData();
        this._patientService.clearData();
        this._patientCacheService.clearCache();
        this._requestService.clearData();
        this._expertRequestService.clearData();

        this.reset();
        this.logoutWithReturnUrl();
    }

    private getLogoutReturnUrl(): string {
        if (Capacitor.isNativePlatform()) {
            return `com.dipitatouch.app://sign-in`;
        }
        return `${this._document.location.origin}/#/sign-in`;
    }

    logoutWithReturnUrl(): void {
        const returnUrl = this.getLogoutReturnUrl();

        // Logout - le service gère maintenant la navigation automatiquement
        this._authService.logout({
            logoutParams: {
                returnTo: returnUrl
            }
        });
    }

    reset(): void {
        this._isInitialized = false;
    }
}